import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, Music, Users, MapPin, Ticket } from "lucide-react";
import { Reveal, Stagger, StaggerItem } from "../components/Reveal";
import { Eyebrow, Button } from "../components/Bits";
import Editable from "../components/Editable";
import ProjectGallery from "../components/ProjectGallery";
import { IMG, GALLERIES } from "../lib/content";

const whatsOn = [
  { icon: Music, title: "Main Stage", body: "Running 1pm till 7pm on Sunday, headlined by the Vengaboys." },
  { icon: Users, title: "Pride Parade", body: "The Pride by the Harbourside annual parade kicks off at 12:15 from the Queen's Dock, marching to the main arena." },
  { icon: MapPin, title: "Eat Street & Drink", body: "Lakeland Life Events host Eat Street all day on the Harbourside." },
  { icon: Ticket, title: "Local Business Market", body: "Plenty of traders lined up all across Whitehaven Harbourside." },
];

export default function WhitehavenWeekender() {
  return (
    <div>
      <section className="relative overflow-hidden bg-nead-ink text-white pt-[74px]">
        <div className="absolute inset-0">
          <img src={IMG.weekender} alt="" className="h-full w-full object-cover opacity-25 grayscale" />
          <div className="absolute inset-0 bg-gradient-to-t from-nead-ink via-nead-ink/85 to-nead-ink/60" />
        </div>
        <div className="relative container-x pt-14 pb-16 md:pt-20 md:pb-20">
          <Link to="/work" className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white mb-6"><ArrowLeft className="h-4 w-4" /> Our Work</Link>
          <Eyebrow className="text-nead-cyan">Festival · 29-30 August 2026 · Whitehaven Harbour</Eyebrow>
          <motion.h1 initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="mt-5 font-display font-bold tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.03] max-w-4xl">
            Whitehaven <span className="text-gradient">Weekender</span>
          </motion.h1>
          <Editable id="ww.hero.intro" as="p" multiline className="mt-6 max-w-2xl text-lg text-white/70 leading-relaxed">
            A weekend of music, creativity and community, created through a partnership between the teams behind Harbour Fest (Rosehill) and Pride by the Harbourside (NEAD CIC).
          </Editable>
          <div className="mt-8 flex flex-wrap gap-3">
            <a href="https://whitehavenweekender.co.uk" target="_blank" rel="noreferrer" data-testid="ww-official-hero" className="group inline-flex items-center gap-2 rounded-full bg-nead-pink px-6 py-3 text-sm font-semibold text-white hover:bg-white hover:text-nead-navy transition-colors">
              Visit the festival site <ArrowUpRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      </section>

      <section className="container-x py-20 md:py-28">
        <div className="grid lg:grid-cols-[0.4fr_0.6fr] gap-10 lg:gap-16">
          <Reveal><Eyebrow className="text-nead-pink">The partnership</Eyebrow></Reveal>
          <Reveal delay={0.05}>
            <Editable id="ww.partnership.heading" as="h2" className="font-display font-bold text-2xl sm:text-3xl leading-tight tracking-tight text-nead-navy">Two much-loved events, one joyful weekend.</Editable>
            <div className="mt-6 space-y-5 text-nead-navy/70 text-lg leading-relaxed">
              <Editable id="ww.partnership.p1" as="p" multiline>Whitehaven Weekender is a new two-day celebration of music, creativity and community, bringing together the teams behind Harbour Fest (Rosehill) and Pride by the Harbourside (NEAD CIC).</Editable>
              <Editable id="ww.partnership.p2" as="p" multiline>Taking place across the harbourside on Saturday 29th and Sunday 30th August 2026, it's set to be a joyful, inclusive weekend for locals and visitors alike. Across the weekend you can expect free events, performances and activities, alongside big moments worth planning your day around.</Editable>
              <Editable id="ww.partnership.p3" as="p" multiline>The main arena on Sunday 30 August is ticketed (free with a £3.50 booking fee), but the heart of the festival is open, welcoming and made for everyone.</Editable>
              <p className="text-nead-navy/55 text-base">For full festival information, tickets and the Saturday programme, head to the official festival website.</p>
            </div>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href="https://whitehavenweekender.co.uk" target="_blank" rel="noreferrer" data-testid="ww-official" className="group inline-flex items-center gap-2 rounded-full bg-nead-pink px-6 py-3 text-sm font-semibold text-white hover:bg-nead-navy transition-colors">whitehavenweekender.co.uk <ArrowUpRight className="h-4 w-4" /></a>
              <a href="https://rosehilltheatre.co.uk/whitehaven-weekender" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full border-2 border-nead-navy/20 px-6 py-3 text-sm font-semibold text-nead-navy hover:border-nead-pink hover:text-nead-pink transition-colors">Saturday programme (Rosehill)</a>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-nead-mist">
        <div className="container-x py-20 md:py-28">
          <Reveal><Eyebrow className="text-nead-pink">What's on · Sunday 30 August</Eyebrow>
          <h2 className="mt-5 font-display font-bold text-3xl sm:text-4xl tracking-tight text-nead-navy max-w-2xl">Pride at Whitehaven Weekender.</h2></Reveal>
          <Stagger className="mt-12 grid sm:grid-cols-2 gap-6">
            {whatsOn.map((w) => (
              <StaggerItem key={w.title}>
                <div className="rounded-[1.5rem] bg-white border border-black/[0.07] p-8 h-full">
                  <div className="flex h-11 w-11 items-center justify-center rounded-full grad-pink-cyan"><w.icon className="h-5 w-5 text-white" /></div>
                  <h3 className="mt-5 font-display font-bold text-xl text-nead-navy">{w.title}</h3>
                  <p className="mt-2 text-nead-navy/60 leading-relaxed">{w.body}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
          <p className="mt-8 text-nead-navy/55">There is so much more planned for Pride at Whitehaven Weekender, we're not releasing all the details just yet. Please note there is no main stage on Saturday 29 August (HarbourFest), only Sunday 30 August (Pride).</p>
        </div>
      </section>

      <section className="container-x py-20 md:py-28">
        <Reveal><Eyebrow className="text-nead-pink">Gallery</Eyebrow>
        <h2 className="mt-5 font-display font-bold text-3xl sm:text-4xl tracking-tight text-nead-navy mb-10">A weekend to remember.</h2></Reveal>
        <ProjectGallery galleryKey="ww" fallback={GALLERIES.ww} label="Whitehaven Weekender" />
      </section>

      <section className="container-x pb-24">
        <div className="rounded-[1.75rem] bg-nead-ink text-white p-10 md:p-14 text-center">
          <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight">Book your free arena tickets.</h2>
          <p className="mt-4 text-white/70 max-w-xl mx-auto">Tickets are free with a £3.50 booking fee and are only needed for the Pride by the Harbourside arena on Sunday 30 August.</p>
          <div className="mt-8 flex justify-center">
            <a href="https://whitehavenweekender.co.uk" target="_blank" rel="noreferrer" data-testid="ww-tickets" className="group inline-flex items-center gap-2 rounded-full bg-nead-pink px-7 py-3.5 text-sm font-semibold text-white hover:bg-white hover:text-nead-navy transition-colors">Book now <ArrowUpRight className="h-4 w-4" /></a>
          </div>
        </div>
      </section>
    </div>
  );
}
